import {useEffect, useMemo, useRef, useState} from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import {c} from "../landingTokens";
import {getDefaultSipAudioConfig, isSipConfigured, useSipAudioClient} from "../../../utils/sipAudio";
import {VoiceOrbGlow} from "../../../components/voice-orb";
import {CallOrb} from "./CallOrb";
export const LiveCallDemo = ({size = 240, hero = false}) => {
  const config = useMemo(() => getDefaultSipAudioConfig(), []);
  const sip = useSipAudioClient(config);
  const [open, setOpen] = useState(false);
  const configured = isSipConfigured(config);

  const handleStart = async () => {
    setOpen(true);
    try {
      await sip.call();
    } catch {
      // sip.error already carries the message; CallOrb surfaces it.
    }
  };

  const handleClose = () => setOpen(false);

  // Keep a live reference to handleStart so the window-event listener below always invokes the
  // current sip.call closure (sip is recreated on every status change — a captured one goes stale).
  const startRef = useRef(handleStart);
  startRef.current = handleStart;

  // Lets other parts of the page open the live-call console (e.g. the header's "Test it now"
  // button) via openLiveCall(). When SIP isn't configured there's nothing to dial, so we just
  // bring the hero orb — with its "coming soon" state — into view instead.
  useEffect(() => {
    const handleOpen = () => {
      if (configured) {
        startRef.current();
      } else {
        document.getElementById("live")?.scrollIntoView({behavior: "smooth", block: "start"});
      }
    };
    window.addEventListener("open-live-call", handleOpen);
    return () => window.removeEventListener("open-live-call", handleOpen);
  }, [configured]);

  return (
    <Stack className="live-call-demo" alignItems="center" gap={hero ? 2.75 : 0} sx={{position: "relative", zIndex: 1}}>
      <Tooltip title={configured ? "" : "Ask your team to switch on REACT_APP_SIP_* to enable this demo"}>
        {/* Bare Box gives Tooltip a ref-forwarding child and anchors the ring pulses —
            VoiceOrbGlow owns its own click/keyboard handling via `onStart`. */}
        <Box sx={{position: "relative", width: size, height: size, display: "grid", placeItems: "center"}}>
          {/* Soft breathing halo — lifts the orb off the white card so it reads as a glowing,
              live presence rather than a dark sphere. */}
          {hero && (
            <Box aria-hidden className="hero-orb-halo" sx={{
              position: "absolute",
              inset: "-17%",
              borderRadius: "50%",
              pointerEvents: "none",
              filter: "blur(8px)",
              background: `radial-gradient(circle at 50% 50%, ${c.purpleMist} 0%, rgba(199,210,254,0.35) 44%, rgba(255,255,255,0) 70%)`,
            }}/>
          )}
          {hero && configured && [0, 1, 2].map((i) => (
            <Box
              key={i}
              aria-hidden
              className="hero-orb-ring"
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `1.5px solid ${c.purple}`,
                animationDelay: `${i * 1.05}s`,
                pointerEvents: "none",
              }}
            />
          ))}
          <VoiceOrbGlow
            state="idle"
            active={open}
            size={size}
            backgroundColor={c.white}
            onStart={configured ? handleStart : undefined}
          />
        </Box>
      </Tooltip>

      {hero && (
        <Stack alignItems="center" gap={1.1} sx={{maxWidth: 330}}>
          <Typography sx={{fontSize: {xs: 19, md: 22}, fontWeight: 650, letterSpacing: "-0.02em", color: c.ink}}>
            Talk to OpenBrain Assistant
          </Typography>
          <Typography sx={{fontSize: 13.5, lineHeight: 1.55, color: c.inkSoft, textAlign: "center"}}>
            OpenBrain's own voice assistant — ask anything about the platform, or book a demo,
            live over a call.
          </Typography>
          {/* Tap-to-talk affordance — reinforces that the orb is a live, clickable call. */}
          {/*<Box*/}
          {/*  onClick={configured ? handleStart : undefined}*/}
          {/*  role={configured ? "button" : undefined}*/}
          {/*  aria-label={configured ? "Start a call with Nova" : undefined}*/}
          {/*  sx={{*/}
          {/*    mt: 0.25,*/}
          {/*    display: "inline-flex",*/}
          {/*    alignItems: "center",*/}
          {/*    gap: 0.75,*/}
          {/*    px: 2,*/}
          {/*    py: 0.9,*/}
          {/*    borderRadius: "980px",*/}
          {/*    fontFamily: mono,*/}
          {/*    fontSize: 11,*/}
          {/*    letterSpacing: "0.12em",*/}
          {/*    textTransform: "uppercase",*/}
          {/*    color: configured ? c.purpleDeep : c.inkMuted,*/}
          {/*    bgcolor: configured ? "rgba(129,140,248,0.10)" : "rgba(20,21,26,0.04)",*/}
          {/*    cursor: configured ? "pointer" : "default",*/}
          {/*    transition: "background-color .2s ease",*/}
          {/*    "&:hover": configured ? {bgcolor: "rgba(129,140,248,0.18)"} : {},*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <MicRounded sx={{fontSize: 15}}/> {configured ? "Tap to talk" : "Coming soon"}*/}
          {/*</Box>*/}
        </Stack>
      )}

      {/* Mounted as soon as the widget exists so sip.js has an <audio> element ready before it dials. */}
      <audio ref={sip.remoteAudioRef} hidden autoPlay playsInline />

      {open && <CallOrb sip={sip} onClose={handleClose} />}
    </Stack>
  );
};
